import { StanButton, StanModal } from '@/components';
import { useModal } from '@/hooks/useModal';
import { cn, stanNotify } from '@/lib';
import { ButtonHTMLAttributes } from 'react';
import { useNavigate } from 'react-router-dom';
import { tv } from 'tailwind-variants';
import { PreviewSize } from '../constants/editorThemeConstants';
import { useCustomizationStore } from '../stores/customizationStore';
import { useThemeEditorStore } from '../stores/themeEditorStore';
import { useMenuNavigation } from './Menu';
import { Button } from './ui/Button';
import { Icon } from './ui/Icon';
import { Publish } from './ui/Publish';

const PreviewSizeButton = ({
    active = false,
    ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) => {
    const variants = tv({
        base: 'cursor-pointer p-2 text-[20px] hover:!text-brand-main transition-colors duration-100 ease-linear',
        variants: {
            active: {
                true: 'pointer-events-none',
                false: 'text-theme-fg-inactive',
            },
        },
    });

    return <button type='button' className={variants({ active })} {...rest} />;
};

export const Navigation = () => {
    const { previewSize, setPreviewSize, setErrorBlockId, setMenuOpen } = useThemeEditorStore();
    const { isDirty, revert, publish, clearPendingItems } = useCustomizationStore();
    const { reset } = useMenuNavigation();
    const navigate = useNavigate();
    const { isOpen: isRevertModalOpen, open: openRevertModal, close: closeRevertModal } = useModal();
    const { isOpen: isNavModalOpen, open: openNavModal, close: closeNavModal } = useModal();

    const onGlobalEditsClick = () => {
        setMenuOpen(true);
        reset();
    };

    const publishStore = async () => {
        clearPendingItems();
        reset(); // Close menu if there are some diff when publishing and syncing with backend.
        const result = await publish();

        if (!result.success) {
            if (result.validation) {
                setErrorBlockId(result.errors?.[0]?.id ?? null);
                stanNotify({
                    type: 'error',
                    title: 'Validation failed',
                    text: 'Please fix the validation errors and try again.',
                    duration: 5000,
                });
            } else {
                stanNotify({
                    type: 'error',
                    title: 'Failed to publish your store.',
                    text: 'Please try again. If the problem persists, please contact us at friends@stanwith.me',
                    duration: 5000,
                });
            }
            return;
        }

        // Notify user of success
        stanNotify({
            type: 'success',
            title: 'Store published!',
            text: 'Your changes are now live',
            duration: 3000,
        });
    };

    return (
        <div className='border-neutral-subtle grid h-full grid-cols-[auto_auto] items-center border-b px-4 py-2 md:grid-cols-[1fr_1fr] md:px-12'>
            <div>
                <Button
                    icon={<Icon name='chevron' />}
                    className='pl-0'
                    onClick={() => {
                        if (isDirty) openNavModal();
                        else navigate('/');
                    }}
                >
                    My Store
                </Button>
            </div>

            <div className='flex flex-row items-center justify-end gap-3 whitespace-nowrap'>
                <div className='hidden flex-row items-center justify-center gap-1 md:flex'>
                    <PreviewSizeButton
                        active={previewSize === PreviewSize.DESKTOP}
                        onClick={() => setPreviewSize(PreviewSize.DESKTOP)}
                    >
                        <Icon name='desktop' />
                    </PreviewSizeButton>
                    <PreviewSizeButton
                        active={previewSize === PreviewSize.MOBILE}
                        onClick={() => setPreviewSize(PreviewSize.MOBILE)}
                    >
                        <Icon name='mobile' />
                    </PreviewSizeButton>
                </div>

                <div className='flex md:hidden'>
                    <Button
                        className={cn(
                            'text-stan-950 flex size-9 items-center justify-center rounded-full border border-neutral-200 p-0'
                            // 'bg-stan-950 border-stan-950 text-theme-subtle'
                        )}
                        icon={<Icon className='relative left-[2px]' name='brush' />}
                        onClick={onGlobalEditsClick}
                    />
                </div>

                <Publish
                    onClick={publishStore}
                    revertToLastPublished={openRevertModal}
                    variant={isDirty ? 'pending' : 'published'}
                    unsavedChanges={isDirty}
                />

                <StanModal id='revert-modal' isOpen={isRevertModalOpen} onClose={closeRevertModal}>
                    <div className='flex flex-col items-center justify-center gap-2 text-center'>
                        <h2 className='px-10'>Revert to Last Published Version?</h2>
                        <p className='text-[var(--text-color-gray)]'>
                            This will revert your changes to the last published version of your theme. This action
                            cannot be undone.
                        </p>
                        <div className='mt-3 flex flex-wrap justify-center gap-x-2'>
                            <StanButton name='revert' color='secondary' onClick={closeRevertModal}>
                                Keep Editing
                            </StanButton>
                            <StanButton
                                name='revert'
                                onClick={() => {
                                    revert();
                                    closeRevertModal();
                                }}
                            >
                                Revert Changes
                            </StanButton>
                        </div>
                    </div>
                </StanModal>

                <StanModal id='confirm-navigation-modal' isOpen={isNavModalOpen} onClose={() => closeNavModal()}>
                    <div className='flex flex-col items-center justify-center gap-2 text-center'>
                        <h2 className='px-10'>Unsaved changes!</h2>
                        <p className='text-[var(--text-color-gray)]'>
                            This will take you to the dashboard page. This means that any changes you make here will not
                            be saved.
                        </p>
                        <div className='mt-3 flex flex-wrap justify-center gap-x-2'>
                            <StanButton
                                name='cancel-product-layout-navigation'
                                color='secondary'
                                onClick={() => closeNavModal()}
                            >
                                Cancel
                            </StanButton>
                            <StanButton
                                name='product-layout-navigation'
                                onClick={() => {
                                    closeNavModal();
                                    navigate('/');
                                }}
                            >
                                Take me to the dashboard page
                            </StanButton>
                        </div>
                    </div>
                </StanModal>
            </div>
        </div>
    );
};
